import { Button, Input, Textarea } from '../components/admin/ui';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  User,
  AlertTriangle,
  Loader2,
  Send,
  Lock,
  X,
  Plus,
  Tag,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, Conversation, Message, Profile, CannedResponse } from '../lib/admin-api';
import { supabase } from '../lib/supabase';

export function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [agents, setAgents] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Reply composer state
  const [replyText, setReplyText] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Canned responses state
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [showCannedPicker, setShowCannedPicker] = useState(false);
  const [cannedFilter, setCannedFilter] = useState('');

  // Tags state
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [orgTags, setOrgTags] = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchData() {
      if (!id || !profile?.organization_id) return;

      setLoading(true);
      const [convData, messagesData, agentsData] = await Promise.all([
        adminApi.getConversation(id),
        adminApi.getConversationMessages(id),
        adminApi.getAgents(profile.organization_id),
      ]);

      setConversation(convData);
      setMessages(messagesData);
      setAgents(agentsData);
      setLoading(false);

      adminApi.getCannedResponses(profile.organization_id).then(setCannedResponses);
      adminApi.getOrgTags(profile.organization_id).then(setOrgTags);
    }

    fetchData();
  }, [id, profile?.organization_id]);

  // Realtime subscription for new messages
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel(`conv-detail-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${id}`,
        },
        async (payload) => {
          const { data } = await supabase
            .from('messages')
            .select('*, sender:profiles(id, display_name, avatar_url)')
            .eq('id', payload.new.id)
            .single();

          if (data) {
            setMessages((prev) => {
              if (prev.find((m) => m.id === data.id)) return prev;
              return [...prev, data];
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    setUpdating(true);
    const success = await adminApi.updateConversation(id, { status: newStatus });
    if (success) {
      setConversation((prev) => prev ? { ...prev, status: newStatus } : null);
    }
    setUpdating(false);
  };

  const handleAssignmentChange = async (agentId: string) => {
    if (!id) return;
    setUpdating(true);
    const success = await adminApi.updateConversation(id, {
      assigned_agent_id: agentId || null,
    });
    if (success) {
      const agent = agents.find((a) => a.id === agentId);
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              assigned_agent_id: agentId || null,
              agent: agent
                ? { id: agent.id, display_name: agent.display_name || '', avatar_url: agent.avatar_url }
                : undefined,
            }
          : null
      );
    }
    setUpdating(false);
  };

  const handlePriorityChange = async (newPriority: string) => {
    if (!id) return;
    setUpdating(true);
    const success = await adminApi.updateConversation(id, { priority: newPriority });
    if (success) {
      setConversation((prev) => prev ? { ...prev, priority: newPriority } : null);
    }
    setUpdating(false);
  };

  const handleSendReply = async () => {
    if (!id || !replyText.trim() || sending) return;
    setSending(true);

    try {
      const { data: message, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: id,
          sender_id: profile?.id,
          sender_type: 'agent',
          content: replyText.trim(),
          content_type: 'text',
          is_private: isPrivate,
        })
        .select('*, sender:profiles(id, display_name, avatar_url)')
        .single();

      if (error) throw error;

      if (message) {
        setMessages((prev) => {
          if (prev.find((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }

      setReplyText('');
      setIsPrivate(false);
      textareaRef.current?.focus();
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handleReplyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setReplyText(value);

    if (value.startsWith('/') && value.length > 1) {
      setShowCannedPicker(true);
      setCannedFilter(value.slice(1));
    } else if (value === '/') {
      setShowCannedPicker(true);
      setCannedFilter('');
    } else {
      setShowCannedPicker(false);
    }
  };

  const insertCannedResponse = (response: CannedResponse) => {
    setReplyText(response.content);
    setShowCannedPicker(false);
    textareaRef.current?.focus();
  };

  const filteredCanned = cannedResponses.filter(
    (r) =>
      !cannedFilter ||
      r.short_code.toLowerCase().includes(cannedFilter.toLowerCase()) ||
      r.title.toLowerCase().includes(cannedFilter.toLowerCase())
  );

  const handleAddTag = useCallback(async (tag: string) => {
    if (!id || !conversation) return;
    const trimmed = tag.trim().toLowerCase();
    if (!trimmed || (conversation.tags || []).includes(trimmed)) return;

    const newTags = [...(conversation.tags || []), trimmed];
    const success = await adminApi.updateConversation(id, { tags: newTags } as Partial<Conversation>);
    if (success) {
      setConversation((prev) => prev ? { ...prev, tags: newTags } : null);
      if (!orgTags.includes(trimmed)) {
        setOrgTags((prev) => [...prev, trimmed]);
      }
    }
    setTagInput('');
    setShowTagInput(false);
  }, [id, conversation, orgTags]);

  const handleRemoveTag = async (tag: string) => {
    if (!id || !conversation) return;
    const newTags = (conversation.tags || []).filter((t: string) => t !== tag);
    const success = await adminApi.updateConversation(id, { tags: newTags } as Partial<Conversation>);
    if (success) {
      setConversation((prev) => prev ? { ...prev, tags: newTags } : null);
    }
  };

  const getSenderIcon = (senderType: string) => {
    switch (senderType) {
      case 'ai_agent':
        return <Bot className="w-5 h-5 text-info-foreground" />;
      case 'agent':
        return <User className="w-5 h-5 text-link" />;
      case 'system':
        return <AlertTriangle className="w-5 h-5 text-warning-foreground" />;
      default:
        return <User className="w-5 h-5 text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-link" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Conversation not found</p>
        <Button variant="ghost"
          onClick={() => navigate('/conversations')}
          className="mt-4 text-link hover:text-link"
        >
          Back to conversations
        </Button>
      </div>
    );
  }

  return (
    <div className="support-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button aria-label="Go back" size="iconSm" variant="ghost"
          onClick={() => navigate('/conversations')}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="support-page-title">
            {conversation.title || `Conversation #${conversation.id.slice(0, 8)}`}
          </h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span className="capitalize">{conversation.channel}</span>
            <span>·</span>
            <span>{new Date(conversation.created_at).toLocaleString()}</span>
            {conversation.ai_handled && (
              <>
                <span>·</span>
                <span className="inline-flex items-center text-info-foreground">
                  <Bot className="w-4 h-4 mr-1" />
                  AI Handled
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages + Reply Composer */}
        <div className="lg:col-span-2 flex flex-col bg-card rounded-lg border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Messages</h2>
          </div>

          <div className="flex-1 p-6 space-y-4 max-h-[500px] overflow-y-auto">
            {messages.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No messages yet</p>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.is_private
                      ? 'bg-warning border border-border rounded-lg p-3 -mx-1'
                      : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      {message.is_private ? (
                        <Lock className="w-4 h-4 text-warning-foreground" />
                      ) : (
                        getSenderIcon(message.sender_type)
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">
                        {message.sender?.display_name ||
                          (message.sender_type === 'ai_agent' ? 'AI Assistant' : 'User')}
                      </span>
                      {message.is_private && (
                        <span className="text-xs bg-warning text-warning-foreground px-1.5 py-0.5 rounded font-medium">
                          Private Note
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(message.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="mt-1 text-foreground whitespace-pre-wrap">
                      {message.content}
                    </div>
                    {message.ai_confidence && (
                      <div className="mt-2 text-xs text-info-foreground">
                        AI Confidence: {Math.round(message.ai_confidence * 100)}%
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Composer */}
          <div className="border-t border-border p-4">
            {showCannedPicker && filteredCanned.length > 0 && (
              <div className="mb-2 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredCanned.map((response) => (
                  <Button variant="ghost"
                    key={response.id}
                    onClick={() => insertCannedResponse(response)}
                    className="w-full text-left px-4 py-2 hover:bg-muted border-b border-border last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-link">/{response.short_code}</span>
                      <span className="text-sm text-muted-foreground">— {response.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{response.content}</p>
                  </Button>
                ))}
              </div>
            )}

            <div className="flex items-start gap-2">
              <Button variant="ghost"
                onClick={() => setIsPrivate(!isPrivate)}
                className={`flex-shrink-0 mt-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  isPrivate
                    ? 'bg-warning text-warning-foreground border border-border'
                    : 'bg-muted text-muted-foreground hover:bg-border'
                }`}
                title={isPrivate ? 'Sending as private note (only visible to agents)' : 'Click to switch to private note'}
              >
                {isPrivate ? (
                  <span className="flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Note
                  </span>
                ) : (
                  'Reply'
                )}
              </Button>

              <div className="flex-1">
                <Textarea
                  ref={textareaRef}
                  value={replyText}
                  onChange={handleReplyChange}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isPrivate
                      ? 'Write a private note... (only visible to agents)'
                      : 'Type a reply... (/ for canned responses)'
                  }
                  className={`w-full border rounded-lg px-4 py-2.5 text-sm resize-none focus:ring-2 focus:outline-none min-h-[44px] max-h-[120px] ${
                    isPrivate
                      ? 'border-border bg-warning focus:ring-border focus:border-border'
                      : 'border-border focus:ring-ring focus:border-primary'
                  }`}
                  rows={1}
                />
              </div>

              <Button aria-label="Send reply" size="iconSm" variant="ghost"
                onClick={handleSendReply}
                disabled={!replyText.trim() || sending}
                className={`flex-shrink-0 mt-1 p-2.5 rounded-lg transition-colors ${
                  isPrivate
                    ? 'bg-warning hover:bg-warning text-primary-foreground disabled:bg-warning'
                    : 'bg-primary hover:bg-primary text-primary-foreground disabled:bg-primary/15'
                }`}
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Press Enter to send, Shift+Enter for new line
              {!isPrivate && ' · Type / for canned responses'}
            </p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status & Assignment */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4">Details</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="conversationdetailpage-1" className="block text-sm font-medium text-foreground mb-1">
                  Status
                </label>
                <select id="conversationdetailpage-1"
                  value={conversation.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updating}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary disabled:opacity-50"
                >
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                  <option value="snoozed">Snoozed</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <label htmlFor="conversationdetailpage-2" className="block text-sm font-medium text-foreground mb-1">
                  Assigned To
                </label>
                <select id="conversationdetailpage-2"
                  value={conversation.assigned_agent_id || ''}
                  onChange={(e) => handleAssignmentChange(e.target.value)}
                  disabled={updating}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary disabled:opacity-50"
                >
                  <option value="">Unassigned</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.display_name || agent.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="conversationdetailpage-3" className="block text-sm font-medium text-foreground mb-1">
                  Priority
                </label>
                <select id="conversationdetailpage-3"
                  value={conversation.priority}
                  onChange={(e) => handlePriorityChange(e.target.value)}
                  disabled={updating}
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary disabled:opacity-50 ${
                    conversation.priority === 'urgent' ? 'border-destructive/30 text-error-text' :
                    conversation.priority === 'high' ? 'border-border text-warning-foreground' :
                    'border-border'
                  }`}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">Labels</h3>
              <Button aria-label="Add label" size="iconSm" variant="ghost"
                onClick={() => setShowTagInput(!showTagInput)}
                className="text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {(conversation.tags || []).map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/15 text-link text-xs font-medium rounded-full"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                  <Button aria-label="Remove label" size="iconSm" variant="ghost"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-link ml-0.5"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </span>
              ))}
              {(conversation.tags || []).length === 0 && !showTagInput && (
                <p className="text-xs text-muted-foreground">No labels</p>
              )}
            </div>

            {showTagInput && (
              <div className="mt-2">
                <Input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag(tagInput);
                    }
                    if (e.key === 'Escape') {
                      setShowTagInput(false);
                      setTagInput('');
                    }
                  }}
                  placeholder="Type label and press Enter"
                  className="w-full border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
                  autoFocus
                  list="org-tags"
                />
                <datalist id="org-tags">
                  {orgTags.map((tag) => (
                    <option key={tag} value={tag} />
                  ))}
                </datalist>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4">Info</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Type</dt>
                <dd className="text-foreground capitalize">{conversation.type}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Channel</dt>
                <dd className="text-foreground capitalize">{conversation.channel}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd className="text-foreground">
                  {new Date(conversation.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Updated</dt>
                <dd className="text-foreground">
                  {new Date(conversation.updated_at).toLocaleDateString()}
                </dd>
              </div>
              {conversation.csat_rating && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">CSAT</dt>
                  <dd className="text-foreground">{conversation.csat_rating}/5</dd>
                </div>
              )}
              {conversation.sla_breached && (
                <div className="flex items-center gap-2 text-error-text mt-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>SLA Breached</span>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
